/*
 * @providesModule EmailManagement
 *
 */

import React from 'react';

import _ from 'lodash';
import AccountInfoModal from 'AccountInfoModal';
import AccountManagementEmailCell from 'AccountManagementEmailCell';
import AWSManager from 'AWSManager';
import CurrentUserStore from 'CurrentUserStore';
import EditEmailScreen from 'EditEmailScreen';
import I18n, {_m} from 'INTL';
import Label from 'Label';
import LoadingIndicator from 'LoadingIndicator';
import ModalNavigationController from 'ModalNavigationController';
import NavigationBar from 'NavigationBar';
import NavigationManager from 'NavigationManager';
import Optional from 'Optional';
import PushButton from 'PushButton';
import PropTypes from 'prop-types';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import SKZConstants from 'SKZConstants';
import SKZPersonalInfo from 'SKZPersonalInfo';
import styles from 'EmailManagementStyle';
import {TextField} from 'react-native-material-textfield';
import WithdrawFlowController from 'WithdrawFlowController';

import {
  BackHandler,
  Platform,
  View,
  ScrollView,
  TouchableOpacity,
  NativeModules,
} from 'react-native';
import SkillzRouter from 'SkillzRouter';
import withParams from 'withParams';

const SkillzModule = NativeModules.SkillzModule;
const {viewCornerRadiusSecondary} = SkillzStyle.fullStyles;

const EMAIL_VERIFY_POLL_INTERVAL = 10000;

export class EmailManagementPure extends React.PureComponent {
  static propTypes = {
    fromWithdraw: PropTypes.bool,
    onEmailUpdated: PropTypes.func,
    onEmailVerified: PropTypes.func,
  };

  static defaultProps = {};

  constructor(props: Object) {
    super(props);
    this.state = {
      personalInfo: undefined,
      primaryEmail: '',
      userAddingEmail: false,
    };

    AWSManager.trackEvent('MANAGE_EMAIL');
  }

  componentDidMount() {
    if (Platform.OS === 'android') {
      BackHandler.addEventListener('hardwareBackPress', this._onBackPress);
    }

    SkillzModule.setEnabledKeyboardManager(true);
    this._getPageData();
  }

  componentWillUnmount() {
    if (Platform.OS === 'android') {
      BackHandler.removeEventListener('hardwareBackPress', this._onBackPress);
    }

    SkillzModule.setEnabledKeyboardManager(false);
    if (this.verifyEmailInterval) {
      clearInterval(this.verifyEmailInterval);
      this.verifyEmailInterval = null;
    }
  }

  _getPageData() {
    LoadingIndicator.show();
    CurrentUserStore.fetchUserPersonalInfo(false)
      .then((personalInfo: Object) => {
        if (personalInfo) {
          const skzPersonalInfo = new SKZPersonalInfo(personalInfo);

          this.setState(
            {
              personalInfo: skzPersonalInfo,
              primaryEmail: skzPersonalInfo.getUserPrimaryEmailData().email,
            },
            () => {
              LoadingIndicator.hide();
              this._startEmailVerifyPoll();
            }
          );
        } else {
          LoadingIndicator.hide();
        }
      })
      .catch((error: unknown) => {
        LoadingIndicator.hide();
        this._startEmailVerifyPoll();
        console.debug('Error fetching user personal info on EmailManagement screen', error);
      });
  }

  _startEmailVerifyPoll() {
    if (this.props.fromWithdraw && !this.verifyEmailInterval) {
      this.verifyEmailInterval = setInterval(() => {
        console.debug('Email Manager: Verifying user email poll');
        CurrentUserStore.fetchUserPersonalInfo(false).then((userPersonal: Object) => {
          const personal = new SKZPersonalInfo(userPersonal);
          if (personal.hasVerifiedAnEmail()) {
            console.debug('Email Manager: User has verified email');
            if (this.verifyEmailInterval) {
              clearInterval(this.verifyEmailInterval);
              this.verifyEmailInterval = null;
            }

            if (this.props.onEmailVerified != null) {
              this.props.onEmailVerified();
            }
            WithdrawFlowController.displayWithdrawFromVerifiedEmail();
          } else {
            console.debug('Email Manager: User has no verified email');
          }
        });
      }, EMAIL_VERIFY_POLL_INTERVAL);
    }
  }

  _updateEmailProps() {
    if (this.props.onEmailUpdated != null) {
      this.props.onEmailUpdated({
        primaryEmail: this.state.primaryEmail,
        secondaryEmails: this.state.personalInfo.secondary_emails,
      });
    }
  }

  _onBackPress = (): bool => {
    this._updateEmailProps();
    SkillzRouter.back();
    return true;
  };

  _onEditEmailScreenUpdate = (personalInfo: SKZPersonalInfo) => {
    this.setState({personalInfo});
  };

  _onEditPress = () => {
    const gameId = SkillzModule.App.GameId;
    SkillzRouter.push(`/games/${gameId}/account/email/edit`, {
      personalInfo: this.state.personalInfo,
    });
  };

  _isEmailLimitReached(): boolean {
    return this.state.personalInfo.getAllUserEmails().length == SKZConstants.MAX_EMAIL_AMOUNT;
  }

  _isExistingEmail(email: string): boolean {
    const userEmails = this.state.personalInfo.getAllUserEmails();
    let isExistingEmail = false;
    userEmails.some((emailItem: Object): boolean => {
      if (emailItem.email.toUpperCase() == email.toUpperCase()) {
        isExistingEmail = true;
        return true;
      }
      return false;
    });
    return isExistingEmail;
  }

  _onAddEmailPress = () => {
    AWSManager.trackEvent('MANAGE_EMAIL_ADD_EMAIL');
    if (this._isEmailLimitReached()) {
      this._showModal(
        I18n.message('Max Emails Reached'),
        I18n.message('You may only have 5 emails at a time.'),
        true
      );
      return;
    }

    this.setState({userAddingEmail: true});
  };

  _onAddEmailSubmit = (event?: Object) => {
    let email = '';

    if (_.get(event, 'nativeEvent', false)) {
      email = event.nativeEvent.text;
    } else {
      email = _.get(this._addEmailInput, 'state.text', '');
    }

    if (!email) {
      return;
    } else if (this._isExistingEmail(email)) {
      this._showModal(
        I18n.message('Error'),
        I18n.message('This email has already been added.'),
        true
      );
    } else {
      this._addEmail(email);
    }
  };

  _addEmail = (email: string) => {
    if (!this.state.personalInfo) {
      this._showGenericErrorModal();
      NavigationManager.pop();
      return;
    }
    const newPersonalInfo = {
      ...this.state.personalInfo,
      secondary_emails: [...this.state.personalInfo.generateEmailMap().keys(), email],
    };

    LoadingIndicator.show();
    CurrentUserStore.updateUserPersonalInfo(newPersonalInfo)
      .then((updatedPersonalInfo: Object) => {
        if (updatedPersonalInfo) {
          const personalInfo = new SKZPersonalInfo(updatedPersonalInfo);
          this.setState({personalInfo, userAddingEmail: false});
        }
        LoadingIndicator.hide();
      })
      .catch((error: unknown) => {
        LoadingIndicator.hide();
        this._showModal(I18n.message('Error'), error, true);
      });
  };

  _showModal(title: string, body: string, hideCancel: boolean) {
    const modal = (
      <AccountInfoModal
        titleText={title}
        bodyText={body}
        hideCancelButton={hideCancel}
        onDone={this._hideModal}
      />
    );

    const modalDisplayProps = {
      animationType: 'fade',
      transparent: true,
    };

    ModalNavigationController.showModal(modal, modalDisplayProps);
  }

  _showGenericErrorModal() {
    this._showModal(_m('Error'), _m("Uh oh! Doesn't look like that worked. Please try again"));
  }

  _hideModal = () => {
    ModalNavigationController.hideModal();
  };

  _updatePrimaryEmail = (selectedEmail: string): Promise<void> => {
    if (!this.state.personalInfo) {
      this._showGenericErrorModal();
      NavigationManager.pop();
      return;
    }

    if (this.state.personalInfo.email == selectedEmail) {
      return;
    }

    const personal = {...this.state.personalInfo};
    const oldPrimary = _.get(personal, 'email', null) || null;
    const oldSecondary = personal.secondary_emails.find(
      (emailItem: Object): Object => _.get(emailItem, 'email', '') == selectedEmail
    );

    personal.email = selectedEmail;
    oldSecondary.email = oldPrimary;

    const skzPersonal = new SKZPersonalInfo(personal);

    LoadingIndicator.show();
    return CurrentUserStore.updateUserPersonalInfo({
      ...skzPersonal,
      secondary_emails: [...skzPersonal.generateEmailMap().keys()],
    })
      .then((updatedPersonalInfo: Object) => {
        if (updatedPersonalInfo) {
          this._updateUser(selectedEmail, () => {
            this.setState(
              {
                personalInfo: new SKZPersonalInfo(updatedPersonalInfo),
                primaryEmail: updatedPersonalInfo.email,
              },
              () => {
                LoadingIndicator.hide();
              }
            );
          });
        } else {
          LoadingIndicator.hide();
        }
      })
      .catch((error: unknown) => {
        console.debug('Error updating user personal info on EmailManagement', error);
        LoadingIndicator.hide();
      });
  };

  _updateUser(email: string, completion: unknown) {
    CurrentUserStore.fetchUser(true).then((user: Object) => {
      if (user) {
        CurrentUserStore.updateUser({...user, email})
          .then(() => {
            completion();
          })
          .catch((err: unknown) => {
            completion();
            console.debug('Error updating user on EmailManagement', err);
          });
      } else {
        completion();
      }
    });
  }

  _renderEditButton(): React.ReactNode {
    return (
      <TouchableOpacity
        style={styles.rightButton}
        onPress={this._onEditPress}
        accessibilityLabel={'Edit'}
        accessible={true}
      >
        <Label style={styles.rightButtonText}>{I18n.message('Edit')}</Label>
      </TouchableOpacity>
    );
  }

  _renderNavBar(): React.ReactNode {
    let canEdit = false;

    if (this.state.personalInfo) {
      const emails = this.state.personalInfo.getAllUserEmails();
      if (emails.length > 1) {
        canEdit = true;
      }
    }

    return (
      <NavigationBar
        hasBackButton={true}
        onBackButtonPress={this._onBackPress}
        hasRightButton={false}
        hasRightComponent={canEdit}
        renderRightComponent={this._renderEditButton()}
        title={I18n.message('Manage Emails')}
      />
    );
  }

  _renderMustVerify(): React.ReactNode {
    if (this.props.fromWithdraw) {
      return (
        <View>
          <View style={styles.mustVerifyContainer}>
            <Label style={styles.mustVerifyText}>
              {I18n.message('You must verify an email before withdrawing')}
            </Label>
          </View>
          <View style={styles.divider} />
        </View>
      );
    }
  }

  _renderPrimaryEmail(): React.ReactNode | null {
    if (!this.state.primaryEmail) {
      return null;
    }

    return (
      <View>
        <AccountManagementEmailCell
          email={this.state.primaryEmail}
          primary={true}
          verified={_.get(this.state.personalInfo, 'email_verified', false)}
          index={0}
        />
        <View style={styles.divider} />
      </View>
    );
  }

  _renderSecondaryEmails(): React.ReactNode | null {
    const emails = _.get(this.state.personalInfo, 'secondary_emails', []) || [];

    if (emails.length < 1) {
      return null;
    }

    return emails.map((emailItem: Object, index: number): React.ReactNode => {
      return (
        <View key={emailItem.email}>
          <AccountManagementEmailCell
            email={emailItem.email}
            primary={false}
            verified={emailItem.verified}
            onVerifyPassword={this._updatePrimaryEmail}
            index={index + 1}
          />
          <View style={styles.divider} />
        </View>
      );
    });
  }

  _renderPlus(): React.ReactNode {
    return <Label style={styles.plusSign}>{'+'}</Label>;
  }

  _renderAddEmail(): React.ReactNode {
    return (
      <View>
        <Optional test={this.state.userAddingEmail}>
          <View style={styles.divider} />
        </Optional>
        <TouchableOpacity
          style={styles.addEmailContainer}
          onPress={this._onAddEmailPress}
          accessibilityLabel={'Add Email'}
          accessible={true}
        >
          {this._renderPlus()}
          <Label style={styles.blueText}>{I18n.message('Add email')}</Label>
        </TouchableOpacity>
        <View style={styles.divider} />
      </View>
    );
  }

  _renderAddEmailRow(): React.ReactNode | null {
    if (!this.state.userAddingEmail) {
      return null;
    }

    return (
      <View style={styles.addEmailInputContainer}>
        <View style={styles.addEmailInputWrapper}>
          <TextField
            ref={(ref: unknown) => {
              this._addEmailInput = ref;
            }}
            style={styles.addEmailInput}
            inputContainerPadding={4}
            labelHeight={32}
            label={I18n.message('email address')}
            labelTextStyle={styles.addEmailLabel}
            tintColor={'rgb(74, 144, 226)'}
            baseColor={'rgb(172, 172, 172)'}
            activeLineWidth={1}
            autoFocus={true}
            keyboardType={'email-address'}
            returnKeyType={'done'}
            onSubmitEditing={this._onAddEmailSubmit}
            autoCapitalize={'none'}
            autoCorrect={false}
            accessible={true}
            accessibilityLabel={'new_email_field'}
          />
        </View>
        <PushButton
          style={styles.addEmailInputButton}
          onPress={this._onAddEmailSubmit}
          colors={['#FFF']}
          title={I18n.message('Add')}
          textStyle={styles.addEmailInputButtonText}
          cornerRadius={viewCornerRadiusSecondary.borderRadius}
        />
      </View>
    );
  }

  _renderContent(): React.ReactNode {
    return (
      <ScrollView keyboardShouldPersistTaps={'always'} style={styles.container}>
        {this._renderPrimaryEmail()}
        {this._renderSecondaryEmails()}
        {this._renderAddEmailRow()}
        {this._renderAddEmail()}
      </ScrollView>
    );
  }

  _renderPhoneLandscapeAndTablet(): React.ReactNode {
    const bodyContainer = Sizes.isTablet()
      ? styles.tabletContainer
      : styles.landscapePhoneContainer;

    return (
      <View style={styles.flexOne}>
        {this._renderNavBar()}
        <View style={bodyContainer}>
          {this._renderMustVerify()}
          {this._renderContent()}
        </View>
      </View>
    );
  }

  _renderPhonePortrait(): React.ReactNode {
    return (
      <View style={styles.container}>
        {this._renderNavBar()}
        {this._renderMustVerify()}
        {this._renderContent()}
      </View>
    );
  }

  render(): React.ReactNode {
    return Sizes.select({
      phonePortrait: this._renderPhonePortrait(),
      other: this._renderPhoneLandscapeAndTablet(),
    });
  }
}

export default withParams(EmailManagementPure);
